import { toggleEditableBlocks } from '../updateDynamicText/toggleEditableBlocks'
import regEx from 'utils/global/regEx'
import { t } from 'i18next'

/**
 * Kerning plugin for TinyMCE
 * Allows users to adjust the letter-spacing of selected text
 */
export const kerningPlugin = ({
  editor,
  updateHeight,
}: {
  editor: Editor
  updateHeight: () => void
}) => {
  const { unlockNonEditableBlocks, lockNonEditableBlocks } =
    toggleEditableBlocks(editor)
  const kerningPluginName = 'kerning'

  // Initialize kerning plugin
  editor.on('init', () => {
    editor.formatter.register(kerningPluginName, {
      inline: 'span',
      styles: { 'letter-spacing': '%value' },
    })
  })

  const setKernSpaceAttributeToEmptySpans = () => {
    const allSpans = editor.dom.select('span[style*="letter-spacing"]')
    const hasNoSpans = allSpans.length === 0
    if (hasNoSpans) return

    const emptySpans = allSpans.filter(
      (span) => span.textContent?.trim() === '',
    )
    const hasNoEmptySpans = emptySpans.length === 0
    if (hasNoEmptySpans) return

    emptySpans.forEach((emptySpan) => {
      emptySpan.setAttribute('kern-space', 'true')
    })
  }

  const applyKerning = (value: string) => {
    const cantFormat = !editor?.formatter
    if (cantFormat) {
      console.error('Editor formatter not available')
      return
    }

    // The undo transaction records the change as an undo level, which marks
    // the editor dirty: a box writes its HTML back on blur only when it is
    // dirty (shouldCommitBlurContent). The 'change' event alone does not.
    editor.undoManager.transact(() => {
      unlockNonEditableBlocks()

      editor.formatter.apply(kerningPluginName, { value })
      updateHeight()
      editor.fire('change')

      setKernSpaceAttributeToEmptySpans()
      lockNonEditableBlocks()
    })
  }

  const normalizePointValue = (value: string) => {
    let normalizedValue = value.trim()
    if (!normalizedValue) {
      normalizedValue = '0pt'
    }
    const ptIsMissing = !normalizedValue.endsWith('pt')
    if (ptIsMissing) {
      normalizedValue = `${normalizedValue}pt`
    }
    const isValid = regEx.ptValue.test(normalizedValue)
    if (!isValid) {
      return null
    }
    return normalizedValue
  }

  type KerningItem = {
    type: 'menuitem'
    text: string
    onAction: () => void
  }

  type OnSubmitDialogApi = {
    getData: () => { kerning: string }
    close: () => void
  }

  // Add kerning dropdown
  editor.ui.registry.addMenuButton(kerningPluginName, {
    text: t('editor.tinyMCE.kerning.text'),
    tooltip: t('editor.tinyMCE.kerning.tooltip'),
    fetch: (callback: (items: KerningItem[]) => void) => {
      const label = t('editor.tinyMCE.kerning.label')
      const inputMode = 'decimal'
      const items: KerningItem[] = [
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.veryTight'),
          onAction: () => applyKerning('-0.7pt'),
        },
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.tight'),
          onAction: () => applyKerning('-0.5pt'),
        },
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.normal'),
          onAction: () => applyKerning('0pt'),
        },
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.loose'),
          onAction: () => applyKerning('0.5pt'),
        },
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.veryLoose'),
          onAction: () => applyKerning('1pt'),
        },
        {
          type: 'menuitem',
          text: t('editor.tinyMCE.kerning.custom'),
          onAction: () => {
            const currentValue = (() => {
              const node = editor.selection.getNode()
              const spansWithKerning = node.querySelectorAll(
                'span[style*="letter-spacing"]',
              )
              const dontShowCurrentValue = spansWithKerning.length > 1
              if (dontShowCurrentValue) {
                return ''
              }
              let style = editor.dom.getStyle(node, 'letter-spacing')
              if (!style) {
                const spanWithKerning = node.closest(
                  'span[style*="letter-spacing"]',
                )
                if (spanWithKerning) {
                  style = editor.dom.getStyle(spanWithKerning, 'letter-spacing')
                }
              }
              if (!style) {
                const matchedNode = editor.formatter.match(kerningPluginName)
                if (matchedNode) {
                  const matchedSpan = editor.dom.getParent(
                    node,
                    'span[style*="letter-spacing"]',
                  )
                  if (matchedSpan) {
                    style = editor.dom.getStyle(matchedSpan, 'letter-spacing')
                  }
                }
              }
              return style || '0pt'
            })()
            editor.windowManager.open({
              initialData: {
                kerning: currentValue,
              },
              title: t('editor.tinyMCE.kerning.modalTitle'),
              body: {
                type: 'panel',
                items: [
                  {
                    type: 'input',
                    name: kerningPluginName,
                    label,
                    inputMode,
                    maximized: true,
                    disabled: false,
                    placeholder: currentValue,
                  },
                ],
              },
              buttons: [
                {
                  type: 'cancel',
                  text: t('editor.tinyMCE.kerning.cancelButton'),
                },
                {
                  type: 'submit',
                  text: t('editor.tinyMCE.kerning.applyButton'),
                  primary: true,
                },
              ],
              onSubmit: (api: OnSubmitDialogApi) => {
                const data = api.getData()
                const value = data.kerning
                const normalizedValue = normalizePointValue(value)
                if (!normalizedValue) {
                  const errorMessage = `Invalid value: "${value}". Must be a decimal e.g. -0.7 (tighter), 0 (normal), 1 (looser)`
                  editor.windowManager.alert(errorMessage)
                  return
                }
                applyKerning(normalizedValue)
                api.close()
              },
            })
          },
        },
      ]

      callback(items)
    },
  })
}

export default kerningPlugin
